import { Vector3 } from 'three';
import { EAttributeType, EAttributeUnitFamily } from '../../../@types/AttributeTypes';
import { AttachmentApiType, AttachmentAsset } from '../../../models/standard/AttachmentAsset';
import AttachmentModel, { AttachmentPayload, AttachmentType } from '../../../models/standard/AttachmentModel';
import AttributeModel from '../../../models/standard/AttributeModel';
import HotspotModel from '../../../models/standard/HotspotModel';
import { getDefaultAttributesForObject, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { PvAttachment } from '../../structures/panoviewer/new/@interfaces/@v_1_0/Attachment';
import { PvAttributeAttachment } from '../../structures/panoviewer/new/@interfaces/@v_1_0/AttributeAttachment';
import { PvPanoramaAttachment } from '../../structures/panoviewer/new/@interfaces/@v_1_0/PanoramaAttachment';
import { PvPdfAttachment } from '../../structures/panoviewer/new/@interfaces/@v_1_0/PdfAttachment';
import { PvPictureAttachment } from '../../structures/panoviewer/new/@interfaces/@v_1_0/PictureAttachment';
import { PvWall } from '../../structures/panoviewer/new/@interfaces/@v_1_0/Wall';
import { PvAttachmentType } from '../../structures/panoviewer/new/@types/@v_1_0/AttachmentType';
import { PvAttribute, PvEAttributeType } from '../../structures/panoviewer/new/attributehandling/@v_1_0/PanoViewerAttributes';

export const PANOVIEWER_ROTATION_OFFSET = new Vector3();
export const PANOVIEWER_TO_FLOORPLANNER_UNIT = 2 / 0.121;
export const PANOVIEWER_ELEMENT_TO_FLOORPLANNER_ELEMENT = 1 / 100;

export function isWallStructureClosed(walls: PvWall[]): boolean {
  if (walls.length < 3) {
    return false;
  }

  const nextMap = new Map<string, PvWall>();
  const incomingCount = new Map<string, number>();
  const outgoingCount = new Map<string, number>();

  for (const wall of walls) {
    if (nextMap.has(wall.startEdgeId)) {
      return false;
    }

    nextMap.set(wall.startEdgeId, wall);
    outgoingCount.set(wall.startEdgeId, (outgoingCount.get(wall.startEdgeId) ?? 0) + 1);
    incomingCount.set(wall.endEdgeId, (incomingCount.get(wall.endEdgeId) ?? 0) + 1);
  }

  const nodes = new Set([...incomingCount.keys(), ...outgoingCount.keys()]);

  for (const node of nodes) {
    if ((incomingCount.get(node) ?? 0) !== 1 || (outgoingCount.get(node) ?? 0) !== 1) {
      return false;
    }
  }

  return true;
}

export function convertAttribute(attribute: PvAttribute, objectType?: ObjectType): AttributeModel {
  // 1. Safely and aggressively extract the ACTUAL value
  let safeValue: string | undefined = undefined;

  if (attribute.value !== undefined && attribute.value !== null && attribute.value !== '') {
    if (attribute.type === PvEAttributeType.BOOL || typeof attribute.value === 'boolean') {
      safeValue = attribute.value === 'false' || attribute.value === false ? 'false' : 'true';
    } else if (Array.isArray(attribute.value)) {
      safeValue = JSON.stringify(attribute.value);
    } else {
      safeValue = String(attribute.value);
    }
  }

  const safeType = attribute.type as unknown as EAttributeType;

  // 2. Enforce Floorplanner Default Hidden/Forced Flags
  let isHidden = attribute.hidden ?? false;
  let isForced = false;

  if (objectType) {
    const defaultAttrs = getDefaultAttributesForObject(objectType);
    const matchedDefault = defaultAttrs.find((def) => def.id === attribute.id);

    if (matchedDefault) {
      isHidden = matchedDefault.hidden;
      isForced = matchedDefault.forced;
    }
  }

  // 3. Create the model
  const model = new AttributeModel(
    attribute.id,
    attribute.key,
    safeValue,
    undefined,
    safeType,
    attribute.unit,
    isHidden,
    isForced,
    attribute.ownerId,
    attribute.userId,
    attribute.publicFlag,
    attribute.localization,
    attribute.defaults,
    attribute.validation,
    attribute.unitFamily as unknown as EAttributeUnitFamily | undefined,
  );

  if (safeValue !== undefined) {
    model.value = safeValue;
  }

  return model;
}

export function convertAttributes(attributes: PvAttribute[], objectType?: ObjectType): AttributeModel[] {
  return attributes.map((attr) => convertAttribute(attr, objectType));
}

export function calculateShapeCenterXZ(points: Array<{ x: number; y: number; z: number }>): { x: number; z: number } | undefined {
  if (!points || points.length === 0) return undefined;

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.x > maxX) maxX = point.x;
    if (point.z < minZ) minZ = point.z;
    if (point.z > maxZ) maxZ = point.z;
  }

  return {
    x: (minX + maxX) / 2,
    z: (minZ + maxZ) / 2,
  };
}

export function calculateSmallestWidth(points: Array<{ x: number; y: number; z: number }>): number | undefined {
  if (!points || points.length < 4) return undefined;

  const edgeLengths: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];

    const dx = p2.x - p1.x;
    const dz = p2.z - p1.z;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist > 0.001) {
      edgeLengths.push(dist);
    }
  }

  edgeLengths.sort((a, b) => a - b);

  if (edgeLengths.length >= 2) {
    const longEdges = edgeLengths.slice(-2);

    return longEdges[0];
  }

  return edgeLengths.length > 0 ? edgeLengths[edgeLengths.length - 1] : undefined;
}

export function quaternionToYaw(q: { x: number; y: number; z: number; w: number }) {
  const sinYaw = 2 * (q.w * q.y + q.x * q.z);
  const cosYaw = 1 - 2 * (q.y * q.y + q.x * q.x);

  let yaw = Math.atan2(sinYaw, cosYaw);

  if (yaw < 0) {
    yaw += 2 * Math.PI;
  }

  return yaw;
}

export function convertAttachment(hotspotId: number, attachment: PvAttributeAttachment | PvAttachment): HotspotModel | undefined {
  let attachmentType: AttachmentType | undefined;
  let attachmentAsset: AttachmentAsset | undefined;
  let attachmentData: AttachmentPayload | undefined;

  if (attachment.attachmentType === PvAttachmentType.ATTRIBUTE) {
    attachmentType = AttachmentType.ATTRIBUTE;
    attachmentData = convertAttribute((attachment as PvAttributeAttachment).attribute);
  } else {
    const tmpWorkingAttachment = attachment as unknown as PvPanoramaAttachment | PvPictureAttachment | PvPdfAttachment;
    const pvAttachmentData = tmpWorkingAttachment.attachmentData;

    switch (attachment.attachmentType) {
      case PvAttachmentType.PANORAMA:
        attachmentType = AttachmentType.PANORAMA;
        break;
      case PvAttachmentType.PICTURE:
        attachmentType = AttachmentType.IMAGE;
        break;
      case PvAttachmentType.PDF:
        attachmentType = AttachmentType.PDF;
        break;
      default:
        break;
    }

    attachmentAsset = {
      id: pvAttachmentData.id,
      dataObjectId: pvAttachmentData.dataObjectId,
      creatorUserId: pvAttachmentData.creatorUserId,
      address: pvAttachmentData.address,
      description: pvAttachmentData.description,
      title: pvAttachmentData.title ?? '',
      fileName: pvAttachmentData.filename ?? '',
      type: pvAttachmentData.type as unknown as AttachmentApiType,
      downloadUrl: pvAttachmentData.downloadUrl,
      thumbnailUrl: pvAttachmentData.thumbnailUrl,
      creationDateDataObject: pvAttachmentData.creationDateDataObject ?? 0,
      creationDate: pvAttachmentData.creationDate,
      fileModifiedDate: pvAttachmentData.fileModifiedDate ?? 0,
      lastModifiedDate: pvAttachmentData.lastModifiedDate,
      shootingDate: pvAttachmentData.shootingDate,
      latitude: pvAttachmentData.latitude,
      longitude: pvAttachmentData.longitude,
    };
    attachmentData = attachmentAsset;
  }

  if (attachmentType && attachmentData) {
    const convertedAttachment = new AttachmentModel(attachmentType, attachmentData);
    const hotspotPosition = new Vector3(
      attachment.position.x * PANOVIEWER_TO_FLOORPLANNER_UNIT,
      attachment.position.z * PANOVIEWER_TO_FLOORPLANNER_UNIT,
      attachment.position.y * PANOVIEWER_TO_FLOORPLANNER_UNIT,
    );

    return new HotspotModel(hotspotId, hotspotPosition, convertedAttachment);
  }
}
